import type { Stage, StageProgress } from './fitThesisStages'

interface StageDef {
  key: Stage
  label: string
  fill: string
  active: string
}

/**
 * Three-segment progress: Customer / Competitors / Solution. Each segment
 * fills 0–100% based on how many of the three blocks in that stage are
 * answered. Colours come from the Market / Business / Solution domain ramps,
 * reinforcing the existing ontology.
 */
const STAGES: readonly StageDef[] = [
  { key: 'customer',   label: 'Customer',    fill: 'var(--color-market-500)',   active: 'var(--color-market-700)' },
  { key: 'competitor', label: 'Competitors', fill: 'var(--color-business-500)', active: 'var(--color-business-700)' },
  { key: 'solution',   label: 'Solution',    fill: 'var(--color-solution-700)', active: 'var(--color-solution-800)' },
] as const

export function FitThesisStageIndicator({ progress }: { progress: StageProgress }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-1">
        {STAGES.map(stage => {
          const value = progress[stage.key]
          const pct = Math.max(0, Math.min(1, value / 3))
          return (
            <div
              key={stage.key}
              className="relative h-1 flex-1 overflow-hidden rounded-sm bg-bg-secondary"
              aria-label={`${stage.label}: ${value} of 3`}
            >
              <div
                className="absolute inset-y-0 left-0 w-full origin-left transition-transform duration-200"
                style={{ background: stage.fill, transform: `scaleX(${pct})` }}
              />
            </div>
          )
        })}
      </div>
      <div className="flex gap-1">
        {STAGES.map(stage => {
          const value = progress[stage.key]
          const isActive = value > 0
          return (
            <span
              key={stage.key}
              className="flex-1 truncate font-mono text-[9px] font-semibold uppercase tracking-wider"
              style={{ color: isActive ? stage.active : 'var(--color-fg-muted)' }}
            >
              {stage.label}
            </span>
          )
        })}
      </div>
    </div>
  )
}
